import { message, notification } from "antd";
import axios from "axios";
import qs from "qs";

import { actions } from "../../store/authRedux/actions";
import { actions as commonActions } from "../../store/commonRedux/actions";

let store;
export const setUpInterceptorStore = (_store) => {
  store = _store;
};

/* ریشه‌ی سرویس، جدا از `/api`. مسیرهای OAuth گوگل زیر `/api` نیستند — مرورگر
   خودش آن‌ها را باز می‌کند — و نباید آدرس سرور دو جا نوشته شود. */
export const API_ORIGIN = "https://api.taskamoon.ir";

const API = axios.create({
  baseURL: `${API_ORIGIN}/api/`,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
  },
});

API.interceptors.request.use(
  (config) => {
    const access_token = store.getState().auth.token;
    const org = store.getState().auth?.organization;

    if (org) {
      config.headers["organization"] = org?.uuid;
    }

    if (access_token) {
      config.headers["Authorization"] = `Bearer ${access_token}`;
      config.headers[`Accept-Language`] = "fa";
    }
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

/* The server speaks in two shapes: Laravel's own validation payload
   ({ message, errors: { field: [...] } }) and our ApiResponse envelope
   ({ status, message, data: [] }). `abort()` produces a third — a bare
   { message } — which the old branch fell through to a generic string,
   which is why a wrong OTP used to read «خطای رخ داده است» instead of
   saying the code was wrong. */
const serverMessage = (error, fallback) =>
  error.response?.data?.message || fallback;

/* Laravel sends Retry-After on a throttled response. Telling someone to wait
   is only useful if it says how long. */
const retryAfterText = (error) => {
  const seconds = Number(
    error.response?.headers?.["retry-after"] ??
      error.response?.data?.retry_after,
  );
  if (!Number.isFinite(seconds) || seconds <= 0) return "";
  if (seconds < 60) return ` لطفاً ${Math.ceil(seconds)} ثانیه دیگر تلاش کنید.`;
  return ` لطفاً ${Math.ceil(seconds / 60)} دقیقه دیگر تلاش کنید.`;
};

API.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (!error.response) {
      message.error("خطا در اتصال اینترنت");
    }

    /* Rate limiting. Without this branch a throttled request failed in total
       silence — the login screen's onError is empty and relies on this
       interceptor for everything it shows. */
    else if (error.response.status === 429) {
      notification.warning({
        className: "ds-notif",
        message: "تعداد درخواست‌های شما بیش از حد مجاز است." + retryAfterText(error),
      });
    }

    /* The upload endpoints now cap file size, and a body larger than the
       server's own limit never reaches PHP's validator — it comes back as 413
       with no JSON body at all. */
    else if (error.response.status === 413) {
      notification.error({
        className: "ds-notif",
        message: serverMessage(error, "حجم فایل انتخاب‌شده بیش از حد مجاز است."),
      });
    }

    else if (error.response.status === 401) {
      store.dispatch(actions.logout());
    } else if (error.response.status === 423) {
      /* Only for something the user just did. Plan-gated GETs are fired by
         chrome that is on screen everywhere — the header timer, the chat badge
         — so treating a read as "you need to upgrade" threw the dialog up on
         page load, repeatedly, for an action nobody took. The API answers reads
         with an empty payload now; this keeps older builds of it from filling
         the screen with modals. */
      const method = (error.config?.method || "get").toLowerCase();
      if (!["get", "head", "options"].includes(method)) {
        store.dispatch(
          commonActions.setSubModal({
            feature: error.response?.data?.feature,
            message: error.response?.data?.message,
          }),
        );
      }
    } else if (error.response.status === 403) {
      notification.error({ className: "ds-notif",
        message: `شما دسترسی لازم برای انجام عملیات را ندارید.`,
      });
    } else if (error.response.status === 409) {
      notification.warning({ className: "ds-notif",
        message:
          error.response.data?.message ||
          "این عملیات با وضعیت فعلی اطلاعات قابل انجام نیست.",
      });
    } else if (error.response.status === 404) {
      message.error("اطلاعات موردنظر یافت نشد");
    } else if (error.response.status === 422 || error.response.status === 400) {
      try {
        const errors = error.response.data?.errors;

        if (errors && Object.keys(errors).length) {
          Object.keys(errors).map((err) =>
            // @ts-ignore
            notification.error({ className: "ds-notif", message: `${err}: ${errors[err][0]}` })
          );
        } else {
          /* No `errors` map: either our ApiResponse envelope or a bare
             `abort()`. Both carry the sentence worth showing in `message`. */
          notification.error({
            className: "ds-notif",
            message: serverMessage(error, "خطای رخ داده است"),
          });
        }
      } catch (error) {
        notification.error({ className: "ds-notif", message: "خطای رخ داده است" });
      }
    } else if (error.response.status === 500) {
      message.error("خطایی در سرور رخ داده است");
    } else if (error.response.status === 508) {
      message.error("خطا در اتصال به سرور");
    }

    return Promise.reject(error);
  }
);

export default API;
